import { useState, type ReactElement } from 'react';
import { farbeVon } from '@schnitt/shared';
import { bildUrl } from '../profilbild';
import type { User } from '../api';
import { ContextMenu, type MenuItem } from './ContextMenu';

/**
 * The top of the start page, the way software has one: the name on the
 * left, and on the right two menus instead of a row of buttons -- one for
 * running the installation (admins only), one for oneself. A menu opens
 * where it was pressed and closes on the first tap outside.
 */

export type AdminScreen = 'users' | 'box' | 'ablage' | 'markensets' | 'mail' | 'storage' | 'diagnose' | 'changes';
export type SelfScreen = 'account' | 'rights' | 'plan' | 'feedback';

export function AppBar({
  user,
  unseen,
  version = null,
  offeneVorschlaege = 0,
  onAdmin,
  onSelf,
  onSignOut,
}: {
  user: User;
  unseen: number;
  /** Die laufende Version, 1.0, 1.1, … (28. Oktober) -- steht bei den Release Notes. */
  version?: string | null;
  /** Vorschläge, über die noch niemand entschieden hat. */
  offeneVorschlaege?: number;
  onAdmin: (screen: AdminScreen) => void;
  onSelf: (screen: SelfScreen) => void;
  onSignOut: () => void;
}): ReactElement {
  const [menu, setMenu] = useState<{ kind: 'admin' | 'self'; x: number; y: number } | null>(null);
  const initial = (user.name || user.email).trim().charAt(0).toUpperCase() || '?';

  const open = (kind: 'admin' | 'self') => (e: React.MouseEvent<HTMLButtonElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    setMenu({ kind, x: box.right, y: box.bottom + 6 });
  };

  const adminItems: MenuItem[] = [
    // Jeder Punkt sein eigenes Zeichen (Sascha, 10. September: „Bitte das
    // Verwaltungs menü aufräumen. Doppelungen“) -- vorher trugen zwei Paare
    // dasselbe, und ein Menü, in dem zwei Zeilen gleich aussehen, ist eine
    // Liste zum Lesen statt zum Wiedererkennen.
    { icon: 'users', label: 'Konten', onSelect: () => onAdmin('users') },
    // Seit 7.23 ist Nextcloud der Hauptweg und Box das, was weiterläuft,
    // solange es gebraucht wird. Zwei gleichrangige Einträge sagten das
    // Gegenteil (Sascha, 10. September: „ich sehe im menü immer noch box“).
    // Box steht deshalb *in* der Ablage, einen Tipp weiter.
    { icon: 'wolke', label: 'Ablage', onSelect: () => onAdmin('ablage') },
    // Die Markensets aller Projekte an einer Stelle (7.219).
    { icon: 'brand', label: 'Markensets', onSelect: () => onAdmin('markensets') },
    { icon: 'mail', label: 'Mail', onSelect: () => onAdmin('mail') },
    { icon: 'speicher', label: 'Speicher', onSelect: () => onAdmin('storage') },
    { icon: 'puls', label: 'Diagnose', onSelect: () => onAdmin('diagnose') },
  ];
  const selfItems: MenuItem[] = [
    { icon: 'person', label: 'Mein Konto', onSelect: () => onSelf('account') },
    { icon: 'eye', label: 'Wer darf was', onSelect: () => onSelf('rights') },
    // Die Version steht dort, wo man nach ihr sucht: bei den Release Notes
    // (Sascha, 28. Oktober: „und auch irgendwo eingeblendet“).
    {
      icon: 'brand',
      label: `Release Notes${unseen ? ` (${unseen})` : ''}${version ? ` · Version ${version}` : ''}`,
      onSelect: () => onAdmin('changes'),
    },
    // Der Fahrplan steht bewusst im Nutzermenü und nicht unter Verwaltung:
    // sehen darf ihn jede und jeder, sortieren nur Admins.

    { icon: 'help', label: 'Etwas melden', onSelect: () => onSelf('feedback') },
    { icon: 'back', label: 'Abmelden', onSelect: onSignOut, danger: true },
  ];

  return (
    <header className="appbar">
      <div className="appbar-brand">
        <span className="appbar-mark" aria-hidden="true">S</span>
        <span className="appbar-name">
          SHA TV <span className="accent">Schnitt</span>
        </span>
      </div>
      <nav className="appbar-menus" aria-label="Menü">
        {/* Die Roadmap steht als eigener Knopf und nicht in einem Menü: was
            man nicht sieht, benutzt niemand -- und genau das war der Befund. */}
        <button
          className={`small appbar-menu ${offeneVorschlaege ? 'meldet' : ''}`}
          onClick={() => onSelf('plan')}
          title={offeneVorschlaege ? `${offeneVorschlaege} Vorschläge warten auf eine Entscheidung` : 'Was gebaut wird, und was noch aussteht'}
        >
          Roadmap
          {/* Sie muss auf sich aufmerksam machen: liegen Vorschläge da, über
              die niemand entschieden hat, steht die Zahl daneben. */}
          {offeneVorschlaege ? <span className="zaehler">{offeneVorschlaege}</span> : null}
        </button>
        {user.role === 'admin' ? (
          <button className="small appbar-menu" onClick={open('admin')} aria-haspopup="menu" data-menu="admin">
            Verwaltung ▾
          </button>
        ) : null}
        <button className="small appbar-menu appbar-user" onClick={open('self')} aria-haspopup="menu" data-menu="self">
          {/* Die eigene Farbe steht auch am eigenen Kürzel -- sonst sieht man
              sich als Einzigen nicht in der Farbe, unter der einen alle
              anderen sehen (7.45). */}
          {bildUrl(user.userId, user.avatarAt) ? (
            <img className="avatar" src={bildUrl(user.userId, user.avatarAt) ?? ''} alt="" data-eigenes-bild />
          ) : (
            <span className="avatar" style={{ background: farbeVon(user.userId, user.color) }} aria-hidden="true" data-eigene-farbe>
              {initial}
            </span>
          )}
          <span className="appbar-username">{user.name}</span>
          {unseen ? <span className="dot" aria-label={`${unseen} Neuerungen`} /> : null}
          ▾
        </button>
      </nav>
      {menu ? (
        <ContextMenu x={menu.x} y={menu.y} items={menu.kind === 'admin' ? adminItems : selfItems} onClose={() => setMenu(null)} align="right" />
      ) : null}
    </header>
  );
}
